import { HeroIcons } from '../../lib/utils/heroIcons'
import { useTranslation } from 'next-i18next'
import { useEffect, useMemo, useRef, useState } from 'react'
import routes from '../../config/route'
import Link from '../shared/Link'
import BackgroundPicture from '../shared/BackgroundPicture'
import gtmService from '../../services/gtm.service'

interface FAQItem {
  question: string
  answer: string
}

interface FAQCategory {
  title: string
  questions: FAQItem[]
}

interface IndexedFAQItem extends FAQItem {
  key: string
  categoryTitle: string
  categoryIndex: number
  questionIndex: number
  searchValue: string
}

const calendlyHref = process.env.NEXT_PUBLIC_CALENDLY || routes.contact

const createQuestionKey = (categoryIndex: number, questionIndex: number) =>
  `${categoryIndex}-${questionIndex}`

const normalizeSearchValue = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()

export default function FAQSection() {
  const { t } = useTranslation('index')
  const sectionRef = useRef<HTMLElement | null>(null)
  const mobileCategoryScrollerRef = useRef<HTMLDivElement | null>(null)
  const mobileCategoryButtonRefs = useRef<(HTMLButtonElement | null)[]>([])
  const hasAnimatedRef = useRef(false)
  const [isInView, setIsInView] = useState(false)
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0)
  const [openQuestionKey, setOpenQuestionKey] = useState(
    createQuestionKey(0, 0),
  )
  const [searchQuery, setSearchQuery] = useState('')
  const [isChangingCategory, setIsChangingCategory] = useState(false)
  const categories = useMemo(
    () =>
      t('landing.faq.categories', {
        returnObjects: true,
      }) as FAQCategory[],
    [t],
  )

  const selectedCategory = categories[selectedCategoryIndex] || categories[0]
  const indexedQuestions = useMemo<IndexedFAQItem[]>(
    () =>
      categories.flatMap((category, categoryIndex) =>
        category.questions.map((item, questionIndex) => ({
          ...item,
          key: createQuestionKey(categoryIndex, questionIndex),
          categoryTitle: category.title,
          categoryIndex,
          questionIndex,
          searchValue: normalizeSearchValue(
            `${category.title} ${item.question} ${item.answer}`,
          ),
        })),
      ),
    [categories],
  )
  const normalizedSearchQuery = useMemo(
    () => normalizeSearchValue(searchQuery),
    [searchQuery],
  )
  const searchTokens = useMemo(
    () => normalizedSearchQuery.split(/\s+/).filter(Boolean),
    [normalizedSearchQuery],
  )
  const hasSearchQuery = searchTokens.length > 0
  const searchResults = useMemo(() => {
    if (!hasSearchQuery) return []

    return indexedQuestions.filter((item) =>
      searchTokens.every((token) => item.searchValue.includes(token)),
    )
  }, [hasSearchQuery, indexedQuestions, searchTokens])
  const visibleQuestions = useMemo<IndexedFAQItem[]>(() => {
    if (hasSearchQuery) return searchResults

    return selectedCategory.questions.map((item, questionIndex) => ({
      ...item,
      key: createQuestionKey(selectedCategoryIndex, questionIndex),
      categoryTitle: selectedCategory.title,
      categoryIndex: selectedCategoryIndex,
      questionIndex,
      searchValue: '',
    }))
  }, [hasSearchQuery, searchResults, selectedCategory, selectedCategoryIndex])
  const firstVisibleQuestionKey = visibleQuestions[0]?.key ?? ''
  const hasNoSearchResults = hasSearchQuery && visibleQuestions.length === 0
  const trimmedSearchQuery = searchQuery.trim()
  const searchResultLabel =
    visibleQuestions.length > 0
      ? t(
          visibleQuestions.length > 1
            ? 'landing.faq.resultsPlural'
            : 'landing.faq.resultsSingular',
          {
            count: visibleQuestions.length,
            query: trimmedSearchQuery,
          },
        )
      : t('landing.faq.noResults', { query: trimmedSearchQuery })

  useEffect(() => {
    const node = sectionRef.current
    if (!node) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry.isIntersecting || hasAnimatedRef.current) return

        hasAnimatedRef.current = true
        setIsInView(true)
        observer.disconnect()
      },
      {
        threshold: 0.18,
        rootMargin: '0px 0px -10% 0px',
      },
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const selectCategory = (index: number) => {
    if (index === selectedCategoryIndex && !searchQuery) return
    const category = categories[index]

    if (category) {
      gtmService.contentEngagement({
        contentType: 'faq',
        contentId: String(index),
        contentTitle: category.title,
        interactionType: 'category_select',
      })
    }

    const scrollSelectedCategoryIntoView = () => {
      const scroller = mobileCategoryScrollerRef.current
      const button = mobileCategoryButtonRefs.current[index]

      if (!scroller || !button) return

      scroller.scrollTo({
        left: button.offsetLeft,
        behavior: 'smooth',
      })
    }

    if (index !== selectedCategoryIndex) {
      setIsChangingCategory(true)
      setSelectedCategoryIndex(index)
    }
    setSearchQuery('')
    setOpenQuestionKey(createQuestionKey(index, 0))
    window.requestAnimationFrame(scrollSelectedCategoryIntoView)
  }

  useEffect(() => {
    setOpenQuestionKey(firstVisibleQuestionKey)
  }, [firstVisibleQuestionKey])

  useEffect(() => {
    if (!trimmedSearchQuery) return

    const timeoutId = window.setTimeout(() => {
      gtmService.contentEngagement({
        contentType: 'faq',
        contentTitle: trimmedSearchQuery,
        interactionType: 'search',
      })
    }, 800)

    return () => window.clearTimeout(timeoutId)
  }, [trimmedSearchQuery])

  useEffect(() => {
    if (!isChangingCategory) return

    const timeoutId = window.setTimeout(() => {
      setIsChangingCategory(false)
    }, 180)

    return () => window.clearTimeout(timeoutId)
  }, [isChangingCategory, selectedCategoryIndex])

  return (
    <section ref={sectionRef} className="relative">
      <div className="mx-auto w-full px-3 md:px-10 lg:px-16 xl:px-24 2xl:px-44">
        <div
          data-navtheme="frosted"
          className={`relative overflow-hidden rounded-container border border-white/20 px-5 py-8 shadow-[0_24px_56px_rgba(18,15,62,0.22)] backdrop-blur-xl transition-all duration-700 md:px-10 md:py-12 ${
            isInView ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
          }`}
        >
          <BackgroundPicture
            fallbackSrc="/img/backgrounds/bg-faq.png"
            sources={[
              {
                srcSet: '/img/backgrounds/bg-faq.webp',
                type: 'image/webp',
              },
            ]}
          />
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(145deg,rgba(12,10,42,0.74)_0%,rgba(37,31,128,0.66)_48%,rgba(12,10,42,0.72)_100%)]" />
          <div className="pointer-events-none absolute inset-0 bg-black/10 backdrop-blur-[1px]" />

          <div className="relative z-10">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-shelters-kicker font-medium text-white/70">
                {t('landing.faq.eyebrow')}
              </p>
              <h2 className="mt-3 font-medium text-white text-shelters-section-title">
                {t('landing.faq.title')}
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-white/80 text-shelters-section-body">
                {t('landing.faq.intro')}
              </p>
            </div>

            <div className="desktop:hidden">
              <div className="mx-auto mt-9 max-w-4xl">
                <label htmlFor="faq-search" className="sr-only">
                  {t('landing.faq.searchLabel')}
                </label>
                <div className="relative">
                  <HeroIcons.MagnifyingGlassIcon
                    className="pointer-events-none absolute left-5 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-white/80"
                    strokeWidth={2.2}
                  />
                  <input
                    id="faq-search"
                    type="search"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder={t('landing.faq.searchPlaceholder')}
                    className="h-14 w-full rounded-button border border-white/20 bg-white/10 pl-12 pr-12 text-shelters-body text-white outline-none backdrop-blur-md transition placeholder:text-white/45 focus:border-white/45 focus:bg-white/15 focus:ring-0"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      aria-label={t('landing.faq.clearSearch')}
                      className="absolute right-3 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white/70 transition hover:border-white/30 hover:bg-white/20 hover:text-white"
                    >
                      <HeroIcons.XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              <div className="mx-auto mt-5 max-w-5xl">
                <div
                  ref={mobileCategoryScrollerRef}
                  className="flex gap-2 overflow-x-auto scroll-smooth pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                  {categories.map((category, index) => {
                    const isSelected =
                      !hasSearchQuery && selectedCategoryIndex === index

                    return (
                      <button
                        ref={(node) => {
                          mobileCategoryButtonRefs.current[index] = node
                        }}
                        key={category.title}
                        type="button"
                        onClick={() => selectCategory(index)}
                        className={`inline-flex shrink-0 items-center gap-2 rounded-button border px-4 py-3 text-left transition-all duration-300 ${
                          isSelected
                            ? 'border-white/40 bg-[#57509a]/90 text-white shadow-[0_12px_28px_rgba(16,13,58,0.22)] backdrop-blur-md'
                            : 'border-white/20 bg-[#312b73]/80 text-white/75 backdrop-blur-md hover:border-white/30 hover:bg-[#453e8a]/90 hover:text-white'
                        }`}
                      >
                        <span className="whitespace-nowrap text-shelters-kicker font-medium">
                          {category.title}
                        </span>
                        <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-white/10 px-1.5 text-[0.7rem] font-medium text-white/70">
                          {category.questions.length}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="mx-auto mt-5 max-w-4xl">
                {hasSearchQuery && (
                  <div className="mb-3 flex flex-col gap-2 rounded-inner-card border border-white/15 bg-[#312b73]/65 px-4 py-3 text-white/75 backdrop-blur-md tablet:flex-row tablet:items-center tablet:justify-between">
                    <p className="text-shelters-kicker">{searchResultLabel}</p>
                    {visibleQuestions.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setSearchQuery('')}
                        className="self-start text-shelters-kicker font-medium text-white transition hover:text-white/80 tablet:self-auto"
                      >
                        {t('landing.faq.reset')}
                      </button>
                    )}
                  </div>
                )}

                <div
                  className={`origin-top space-y-3 transition-all duration-200 ease-out ${
                    isChangingCategory
                      ? 'scale-[0.985] opacity-60'
                      : 'scale-100 opacity-100'
                  }`}
                >
                  {hasNoSearchResults ? (
                    <div className="rounded-inner-card border border-white/25 bg-[#312b73]/80 p-5 text-center shadow-[0_14px_30px_rgba(16,13,58,0.16)] backdrop-blur-md">
                      <span className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-inner-card bg-[#6d5fe8] text-white shadow-[0_12px_24px_rgba(16,13,58,0.18)]">
                        <HeroIcons.ChatBubbleLeftRightIcon className="h-5 w-5" />
                      </span>
                      <p className="mt-4 text-shelters-lead font-medium text-white">
                        {t('landing.faq.noAnswerTitle')}
                      </p>
                      <p className="mx-auto mt-2 max-w-xl text-shelters-body text-white/75">
                        {t('landing.faq.noAnswerDescription')}
                      </p>

                      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:justify-center">
                        <Link
                          href={routes.contact}
                          onClick={() =>
                            gtmService.ctaClicked({
                              ctaId: 'faq_no_results_contact',
                              ctaLabel: t('landing.faq.contact'),
                              ctaLocation: 'faq_no_results',
                              destinationUrl: routes.contact,
                              destinationType: 'contact',
                            })
                          }
                          className="inline-flex h-11 items-center justify-center gap-2 rounded-button border border-white/25 bg-white/10 px-4 text-shelters-body font-medium text-white transition hover:border-white/40 hover:bg-white/20"
                        >
                          <HeroIcons.ChatBubbleLeftRightIcon className="h-4 w-4" />
                          {t('landing.faq.contact')}
                        </Link>
                        <Link
                          href={calendlyHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          skipLocaleHandling
                          onClick={() =>
                            gtmService.ctaClicked({
                              ctaId: 'faq_no_results_calendly',
                              ctaLabel: t('landing.faq.bookMeeting'),
                              ctaLocation: 'faq_no_results',
                              destinationUrl: calendlyHref,
                              destinationType: 'calendly',
                            })
                          }
                          className="inline-flex h-11 items-center justify-center gap-2 rounded-button bg-white px-4 text-shelters-body font-medium text-sheltersBlue transition hover:bg-white/90"
                        >
                          <HeroIcons.CalendarDaysIcon className="h-4 w-4" />
                          {t('landing.faq.bookMeeting')}
                          <HeroIcons.ArrowRightIcon className="h-4 w-4" />
                        </Link>
                      </div>
                    </div>
                  ) : (
                    visibleQuestions.map((item) => {
                      const isOpen = openQuestionKey === item.key

                      return (
                        <article
                          key={item.key}
                          className={`overflow-hidden rounded-inner-card border transition-all duration-300 ${
                            isOpen
                              ? 'border-white/40 bg-[#57509a]/90 shadow-[0_12px_28px_rgba(16,13,58,0.22)] backdrop-blur-md'
                              : 'border-white/20 bg-[#312b73]/80 backdrop-blur-md hover:border-white/30 hover:bg-[#453e8a]/90'
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setOpenQuestionKey(isOpen ? '' : item.key)
                              if (!isOpen) {
                                gtmService.contentEngagement({
                                  contentType: 'faq',
                                  contentId: item.key,
                                  contentTitle: item.question,
                                  interactionType: 'question_open',
                                })
                              }
                            }}
                            className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left"
                          >
                            <span>
                              {hasSearchQuery && (
                                <span className="mb-2 inline-flex rounded-full border border-white/15 bg-white/10 px-2.5 py-1 text-[0.72rem] font-medium text-white/65">
                                  {item.categoryTitle}
                                </span>
                              )}
                              <span className="block text-shelters-body font-medium leading-[1.35] text-white">
                                {item.question}
                              </span>
                            </span>
                            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-white/30 bg-white/20 text-white">
                              {isOpen ? (
                                <HeroIcons.MinusIcon className="h-4 w-4" />
                              ) : (
                                <HeroIcons.PlusIcon className="h-4 w-4" />
                              )}
                            </span>
                          </button>

                          <div
                            className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${
                              isOpen
                                ? 'grid-rows-[1fr] opacity-100'
                                : 'grid-rows-[0fr] opacity-0'
                            }`}
                          >
                            <div className="overflow-hidden">
                              <p className="px-4 pb-5 pr-14 text-shelters-body text-white/80">
                                {item.answer}
                              </p>
                            </div>
                          </div>
                        </article>
                      )
                    })
                  )}
                </div>
              </div>
            </div>

            <div className="hidden desktop:block">
              {hasSearchQuery && (
                <div className="mx-auto mt-9 flex max-w-4xl items-center justify-between rounded-inner-card border border-white/15 bg-[#312b73]/65 px-4 py-3 text-white/75 backdrop-blur-md">
                  <p className="text-shelters-kicker">{searchResultLabel}</p>
                  {visibleQuestions.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="text-shelters-kicker font-medium text-white transition hover:text-white/80"
                    >
                      {t('landing.faq.reset')}
                    </button>
                  )}
                </div>
              )}

              <div
                className={`grid gap-6 desktop:grid-cols-[360px_minmax(0,1fr)] desktop:items-start desktop:gap-8 ${
                  hasSearchQuery ? 'mt-5' : 'mt-9'
                }`}
              >
                <div className="flex h-[456px] flex-col gap-3">
                  <div className="relative">
                    <label htmlFor="faq-search-desktop" className="sr-only">
                      {t('landing.faq.searchLabel')}
                    </label>
                    <HeroIcons.MagnifyingGlassIcon
                      className="pointer-events-none absolute left-5 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-white/80"
                      strokeWidth={2.2}
                    />
                    <input
                      id="faq-search-desktop"
                      type="search"
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      placeholder={t('landing.faq.searchPlaceholder')}
                      className="h-14 w-full rounded-button border border-white/20 bg-white/10 pl-12 pr-12 text-shelters-body text-white outline-none backdrop-blur-md transition placeholder:text-white/45 focus:border-white/45 focus:bg-white/15 focus:ring-0"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => setSearchQuery('')}
                        aria-label={t('landing.faq.clearSearch')}
                        className="absolute right-3 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white/70 transition hover:border-white/30 hover:bg-white/20 hover:text-white"
                      >
                        <HeroIcons.XMarkIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <div className="space-y-3">
                    {categories.map((category, index) => {
                      const isSelected =
                        !hasSearchQuery && selectedCategoryIndex === index

                      return (
                        <button
                          key={category.title}
                          type="button"
                          onClick={() => selectCategory(index)}
                          className={`flex w-full items-center justify-between rounded-inner-card border px-4 py-3 text-left transition-all duration-300 ${
                            isSelected
                              ? 'border-white/40 bg-[#57509a]/90 text-white shadow-[0_12px_28px_rgba(16,13,58,0.22)] backdrop-blur-md'
                              : 'border-white/20 bg-[#312b73]/80 text-white/75 backdrop-blur-md hover:border-white/30 hover:bg-[#453e8a]/90 hover:text-white'
                          }`}
                        >
                          <span className="text-shelters-body font-medium">
                            {category.title}
                          </span>
                          <HeroIcons.ChevronRightIcon
                            className={`h-4 w-4 transition-transform duration-300 ${
                              isSelected
                                ? 'translate-x-0.5 text-white'
                                : 'text-white/50'
                            }`}
                          />
                        </button>
                      )
                    })}
                  </div>
                </div>

                <div
                  className={`h-[456px] origin-top space-y-3 overflow-y-auto pr-2 transition-all duration-200 ease-out [scrollbar-color:rgba(255,255,255,0.34)_transparent] [scrollbar-width:thin] ${
                    isChangingCategory
                      ? 'scale-[0.985] opacity-60'
                      : 'scale-100 opacity-100'
                  }`}
                >
                  {hasNoSearchResults ? (
                    <div className="rounded-inner-card border border-white/25 bg-[#312b73]/80 p-5 text-center shadow-[0_14px_30px_rgba(16,13,58,0.16)] backdrop-blur-md">
                      <span className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-inner-card bg-[#6d5fe8] text-white shadow-[0_12px_24px_rgba(16,13,58,0.18)]">
                        <HeroIcons.ChatBubbleLeftRightIcon className="h-5 w-5" />
                      </span>
                      <p className="mt-4 text-shelters-lead font-medium text-white">
                        {t('landing.faq.noAnswerTitle')}
                      </p>
                      <p className="mx-auto mt-2 max-w-xl text-shelters-body text-white/75">
                        {t('landing.faq.noAnswerDescription')}
                      </p>

                      <div className="mt-5 flex justify-center gap-3">
                        <Link
                          href={routes.contact}
                          onClick={() =>
                            gtmService.ctaClicked({
                              ctaId: 'faq_desktop_no_results_contact',
                              ctaLabel: t('landing.faq.contact'),
                              ctaLocation: 'faq_no_results',
                              destinationUrl: routes.contact,
                              destinationType: 'contact',
                            })
                          }
                          className="inline-flex h-11 items-center justify-center gap-2 rounded-button border border-white/25 bg-white/10 px-4 text-shelters-body font-medium text-white transition hover:border-white/40 hover:bg-white/20"
                        >
                          <HeroIcons.ChatBubbleLeftRightIcon className="h-4 w-4" />
                          {t('landing.faq.contact')}
                        </Link>
                        <Link
                          href={calendlyHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          skipLocaleHandling
                          onClick={() =>
                            gtmService.ctaClicked({
                              ctaId: 'faq_desktop_no_results_calendly',
                              ctaLabel: t('landing.faq.bookMeeting'),
                              ctaLocation: 'faq_no_results',
                              destinationUrl: calendlyHref,
                              destinationType: 'calendly',
                            })
                          }
                          className="inline-flex h-11 items-center justify-center gap-2 rounded-button bg-white px-4 text-shelters-body font-medium text-sheltersBlue transition hover:bg-white/90"
                        >
                          <HeroIcons.CalendarDaysIcon className="h-4 w-4" />
                          {t('landing.faq.bookMeeting')}
                          <HeroIcons.ArrowRightIcon className="h-4 w-4" />
                        </Link>
                      </div>
                    </div>
                  ) : (
                    visibleQuestions.map((item) => {
                      const isOpen = openQuestionKey === item.key

                      return (
                        <article
                          key={item.key}
                          className={`overflow-hidden rounded-inner-card border transition-all duration-300 ${
                            isOpen
                              ? 'border-white/40 bg-[#57509a]/90 shadow-[0_12px_28px_rgba(16,13,58,0.22)] backdrop-blur-md'
                              : 'border-white/20 bg-[#312b73]/80 backdrop-blur-md hover:border-white/30 hover:bg-[#453e8a]/90'
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setOpenQuestionKey(isOpen ? '' : item.key)
                              if (!isOpen) {
                                gtmService.contentEngagement({
                                  contentType: 'faq',
                                  contentId: item.key,
                                  contentTitle: item.question,
                                  interactionType: 'question_open',
                                })
                              }
                            }}
                            className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left"
                          >
                            <span>
                              {hasSearchQuery && (
                                <span className="mb-2 inline-flex rounded-full border border-white/15 bg-white/10 px-2.5 py-1 text-[0.72rem] font-medium text-white/65">
                                  {item.categoryTitle}
                                </span>
                              )}
                              <span className="block text-shelters-body font-medium leading-[1.35] text-white">
                                {item.question}
                              </span>
                            </span>
                            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-white/30 bg-white/20 text-white">
                              {isOpen ? (
                                <HeroIcons.MinusIcon className="h-4 w-4" />
                              ) : (
                                <HeroIcons.PlusIcon className="h-4 w-4" />
                              )}
                            </span>
                          </button>

                          <div
                            className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${
                              isOpen
                                ? 'grid-rows-[1fr] opacity-100'
                                : 'grid-rows-[0fr] opacity-0'
                            }`}
                          >
                            <div className="overflow-hidden">
                              <p className="px-4 pb-5 pr-14 text-shelters-body text-white/80">
                                {item.answer}
                              </p>
                            </div>
                          </div>
                        </article>
                      )
                    })
                  )}
                </div>
              </div>
            </div>

            <div className="mt-8 rounded-inner-card border border-white/25 bg-[#312b73]/80 p-4 shadow-[0_14px_30px_rgba(16,13,58,0.16)] backdrop-blur-md md:flex md:items-center md:justify-between md:gap-6 md:p-5">
              <div>
                <p className="text-shelters-lead font-medium text-white">
                  {t('landing.faq.finalTitle')}
                </p>
                <p className="mt-1 max-w-2xl text-shelters-body text-white/75">
                  {t('landing.faq.noAnswerDescription')}
                </p>
              </div>

              <div className="mt-4 flex flex-col gap-3 sm:flex-row md:mt-0 md:shrink-0">
                <Link
                  href={routes.contact}
                  onClick={() =>
                    gtmService.ctaClicked({
                      ctaId: 'faq_final_contact',
                      ctaLabel: t('landing.faq.contact'),
                      ctaLocation: 'faq_final_cta',
                      destinationUrl: routes.contact,
                      destinationType: 'contact',
                    })
                  }
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-button border border-white/25 bg-white/10 px-4 text-shelters-body font-medium text-white transition hover:border-white/40 hover:bg-white/20"
                >
                  <HeroIcons.ChatBubbleLeftRightIcon className="h-4 w-4" />
                  {t('landing.faq.contact')}
                </Link>
                <Link
                  href={calendlyHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  skipLocaleHandling
                  onClick={() =>
                    gtmService.ctaClicked({
                      ctaId: 'faq_final_calendly',
                      ctaLabel: t('landing.faq.bookMeeting'),
                      ctaLocation: 'faq_final_cta',
                      destinationUrl: calendlyHref,
                      destinationType: 'calendly',
                    })
                  }
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-button bg-white px-4 text-shelters-body font-medium text-sheltersBlue transition hover:bg-white/90"
                >
                  <HeroIcons.CalendarDaysIcon className="h-4 w-4" />
                  {t('landing.faq.bookMeeting')}
                  <HeroIcons.ArrowRightIcon className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
